
import { inject, Injectable, DOCUMENT } from '@angular/core'
import * as signalR from '@microsoft/signalr';
import * as Sentry from '@sentry/angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { StateService } from './state.service';
import { SecureDataType } from './securedata.sevice';
import { EncryptDecryptService } from './encrypt-decrypt.service';
import { NotificationService } from './notification.service';

/** Wait before the next start, indexed by failed starts so far: 2 s after a close, then 5 s, 10 s and every 30 s. */
export const HUB_RESTART_DELAYS_MS = [2000, 5000, 10000, 30000];

@Injectable({
    providedIn: 'root'
})
export class SignalRHubService {
    private hubConnection: signalR.HubConnection | undefined;
    private environmentService: EnvironmentService = inject(EnvironmentService);
    #encryptDecryptService: EncryptDecryptService = inject(EncryptDecryptService);
    #appStateService = inject(StateService)
    #signalREventService = inject(SignalREventService)
    #notificationService = inject(NotificationService);
    #document = inject(DOCUMENT);
    #resync = new Subject<void>();

    hubUrl?: string;

    /** Failed starts since the hub was last connected. */
    private failedStarts = 0;
    private restartTimer: ReturnType<typeof setTimeout> | undefined;
    private listening = false;
    private stopped = false;
    /** Set when the connection closed for good or a start failed: stage messages may have been missed. */
    private missedMessages = false;
    private closedNotified = false;

  /**
   * Emits once the hub is connected again after a reconnect, a close or a failed start.
   * The hub has no replay, so a stage the agent started in the gap has to be re-read with getcurrent.
   */
  get Resync(): Observable<void> {
    return this.#resync.asObservable();
  }

  private get isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  protected createConnection(url: string, headers: signalR.MessageHeaders): signalR.HubConnection {
    return new signalR.HubConnectionBuilder()
      .withUrl(url, { headers: headers, withCredentials: true }) // SignalR hub URL
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Debug)
      .build();
  }

  initializeHub(): boolean {
    if (this.hubConnection) return false;
    const sessionId = this.#appStateService.ConnectionId!;
    const headers: signalR.MessageHeaders = { 'X-SARAPlus-ID': this.environmentService.env.azureFDID }
    this.hubUrl = this.environmentService.getSignalrUrl('saraplus-securehub');
    this.hubUrl += `?uniqueid=${sessionId}`;
    this.hubConnection = this.createConnection(this.hubUrl, headers);

    this.hubConnection.onreconnecting(() => {
        this.#notificationService.showWarning(
        'Connection lost. Attempting to reconnect...',
        'Connection Issue'
      );
    });

    this.hubConnection.onreconnected(() => {
      this.#notificationService.showSuccess(
        'Connection has been restored.',
        'Reconnected'
      );
      this.#resync.next();
    });

    this.hubConnection.onclose((message) => {
        // Automatic reconnect gave up: start again instead of leaving the customer without stage updates
        console.log('SignalR connection closed:', message);
        if (this.stopped) return;
        this.missedMessages = true;
        this.closedNotified = true;
        this.#notificationService.showError(
        'Connection to server lost. Trying to reconnect...',
        'Disconnected'
      );
        this.scheduleRestart();
    });

    this.#document.addEventListener('visibilitychange', () => {
      if (this.#document.visibilityState === 'visible') this.restartNow();
    });

    return true;
  }

    startConnection() {
        if (this.initializeHub()) {
            this.start();
        }
    }

    private start(): void {
        const connection = this.hubConnection;
        if (!connection || this.stopped || connection.state !== signalR.HubConnectionState.Disconnected) return;
        this.clearRestartTimer();
        connection
            .start()
            .then(() => {
                console.log('Connection established with SignalR hub');
                if (this.failedStarts > 0) {
                    Sentry.addBreadcrumb({ category: 'signalr', message: `hub started after ${this.failedStarts} failed start(s)`, level: 'info' });
                }
                this.failedStarts = 0;
                if (!this.listening) {
                    this.listening = true;
                    this.startListening();
                    this.ListenToSendMessage();
                }
                if (this.closedNotified) {
                    this.closedNotified = false;
                    this.#notificationService.showSuccess('Connection has been restored.', 'Reconnected');
                }
                if (this.missedMessages) {
                    this.missedMessages = false;
                    this.#resync.next();
                }
            })
            .catch((error) => {
                this.failedStarts++;
                this.missedMessages = true;
                console.warn(`Error connecting to SignalR hub (attempt ${this.failedStarts}):`, error);
                if (this.failedStarts === 1) {
                    // Once per outage, as a warning: the retry below keeps the customer from getting stuck
                    Sentry.captureMessage('SignalR hub start failed, retrying', {
                        level: 'warning',
                        tags: { signalr: 'start_failed' },
                        extra: { error: String(error) }
                    });
                }
                this.scheduleRestart();
            });
    }

    private scheduleRestart(): void {
        if (this.stopped || this.restartTimer) return;
        const delay = HUB_RESTART_DELAYS_MS[Math.min(this.failedStarts, HUB_RESTART_DELAYS_MS.length - 1)];
        this.restartTimer = setTimeout(() => {
            this.restartTimer = undefined;
            // A hidden tab waits for visibilitychange instead of retrying in the background
            if (this.#document.visibilityState === 'hidden') return;
            this.start();
        }, delay);
    }

    private restartNow(): void {
        if (this.hubConnection?.state === signalR.HubConnectionState.Disconnected) {
            this.start();
        }
    }

    private clearRestartTimer(): void {
        if (this.restartTimer) {
            clearTimeout(this.restartTimer);
            this.restartTimer = undefined;
        }
    }

    startListening() {
        this.hubConnection!.on('SecureDataMessage', (encryptedMessage: string) => {

            try {
                const key = this.environmentService.sessionDecryptKey;
                const message: EncryptedSignalRModel = this.#encryptDecryptService.decrypt<EncryptedSignalRModel>(encryptedMessage, key, true);
                const response: SignalRModel = {
                    id: message.Id,
                    type: message.Type,
                    rawData: message.RawData
                };
                this.#signalREventService.RaiseEvent(response);
            }
            catch {
                console.log('SignalR deserealize error', encryptedMessage);
            }
        });
    }

  sendMessage(message: SignalRModel): void {
    if (this.isConnected) {
      this.hubConnection!.invoke('SendMessage', message)
        .catch(err => console.warn('SignalR SendMessage failed:', err));
    }
  }

  sendAlert(uniqueId: string, message: string): void {
    if (this.isConnected) {
      // The socket can still look connected while it is dead (phone locked, tab frozen), and the invoke then
      // rejects when the connection closes. The alert is only progress info for the agent, so drop it.
      this.hubConnection!.invoke('SendAlert', uniqueId, message).then(() => {
        console.log('alert sent');
      }).catch(err => console.warn('SignalR alert not sent:', err));
    }
  }

    closeConnection(): Promise<void> {
        this.stopped = true;
        this.clearRestartTimer();
        if (this.hubConnection) {
            return this.hubConnection.stop()
                .then(() => console.log('Connection stopped'))
                .catch(err => console.log('Error while stopping connection: ' + err));
        }
        return Promise.resolve();
    }

    ListenToSendMessage() {
        this.#signalREventService.SignalRToEvents.subscribe({
            next: (val) => {
                if (val && val.UniqueId) {
                    const message = { Type: val.Type, Data: val.Data };
                    const key = this.environmentService.sessionDecryptKey;
                    const decryptedMessage = this.#encryptDecryptService.encrypt(message, key);
                    this.sendAlert(val.UniqueId, decryptedMessage);
                }
            }
        })
    }
}

@Injectable({
    providedIn: 'root'
})
export class SignalREventService {
    private $eventSubscription = new BehaviorSubject<SignalRModel>({});
    private $events = this.$eventSubscription.asObservable();
    private $eventToSignalRSubscription = new BehaviorSubject<ToSignalRModel>({});
    private $eventToSigalR = this.$eventToSignalRSubscription.asObservable();

    get Events() {
        return this.$events;
    }

    get SignalRToEvents() {
        return this.$eventToSigalR;
    }

    RaiseEvent(model: SignalRModel) {
        this.$eventSubscription.next(model);
    }

    SendEvent(message: ToSignalRModel) {
        this.$eventToSignalRSubscription.next(message);
    }
}

export interface SignalRModel {
    id?: number;
    type?: SecureDataType;
    rawData?: unknown;
}

export interface SignalRTModel<T> extends SignalRModel {
    rawData?: T
}

export interface EncryptedSignalRModel {
    Id?: number;
    Type?: SecureDataType;
    RawData?: unknown;
}

export interface EncryptedSignalRTModel<T> extends EncryptedSignalRModel {
    RawData?: T
}

export interface ToSignalRModel {
    Type?: string,
    Data?: unknown,
    UniqueId?: string
}